import { useState, useEffect } from "react";
import axios from "axios";
import {
  ClockCounterClockwise,
  Motorcycle,
  BatteryHigh,
  CheckCircle,
  User,
  Funnel,
  Storefront,
  IdentificationCard,
  ListChecks,
  Wrench,
  Link,
  ArrowsLeftRight,
  Notepad,
  Prohibit,
  Pencil,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Mapeamento de Ícones para cada ação do Backend
const ACTION_ICONS = {
  // Motos
  motorcycle_created: Motorcycle,
  motorcycle_completed: CheckCircle,
  checklist_updated: ListChecks,
  client_linked: Link,
  observation_added: Notepad,

  // Baterias
  battery_linked: BatteryHigh,
  battery_unlinked: BatteryHigh,
  battery_replaced: ArrowsLeftRight,

  // Revisões
  revision_started: Wrench,
  revision_checklist_updated: ListChecks,
  part_replaced: Wrench,
  revision_completed: CheckCircle,

  // Cadastros - Lojas
  store_created: Storefront,
  store_updated: Pencil,
  store_deactivated: Prohibit,

  // Cadastros - Técnicos
  technician_created: User,
  tech_updated: Pencil,
  tech_deactivated: Prohibit,

  // Cadastros - Templates
  checklist_template_created: ListChecks,
  template_updated: Pencil,
  template_deactivated: Prohibit,

  // Cadastros - Clientes
  client_created: IdentificationCard,
  client_updated: Pencil,

  // Usuarios
  user_register: User,
};

// Mapeamento de Cores Inteligentes
const ACTION_COLORS = {
  // Verdes (Sucesso / Criação / Vínculo / Conclusão)
  motorcycle_created: "text-[#00E676] bg-[#00E676]/10",
  motorcycle_completed: "text-[#00E676] bg-[#00E676]/10",
  battery_linked: "text-[#00E676] bg-[#00E676]/10",
  revision_completed: "text-[#00E676] bg-[#00E676]/10",
  store_created: "text-[#00E676] bg-[#00E676]/10",
  technician_created: "text-[#00E676] bg-[#00E676]/10",
  checklist_template_created: "text-[#00E676] bg-[#00E676]/10",
  client_created: "text-[#00E676] bg-[#00E676]/10",
  client_linked: "text-[#00E676] bg-[#00E676]/10",

  // Amarelos (Edição / Atualização / Troca de Peças)
  checklist_updated: "text-yellow-500 bg-yellow-500/10",
  revision_checklist_updated: "text-yellow-500 bg-yellow-500/10",
  store_updated: "text-yellow-500 bg-yellow-500/10",
  tech_updated: "text-yellow-500 bg-yellow-500/10",
  template_updated: "text-yellow-500 bg-yellow-500/10",
  client_updated: "text-yellow-500 bg-yellow-500/10",
  part_replaced: "text-yellow-500 bg-yellow-500/10",

  // Azuis / Roxos (Início / Informação / Troca de Bateria)
  revision_started: "text-blue-500 bg-blue-500/10",
  battery_replaced: "text-blue-500 bg-blue-500/10",
  observation_added: "text-blue-500 bg-blue-500/10",
  user_register: "text-purple-500 bg-purple-500/10",

  // Vermelhos (Remoção / Desativação)
  battery_unlinked: "text-[#FF3B30] bg-[#FF3B30]/10",
  store_deactivated: "text-[#FF3B30] bg-[#FF3B30]/10",
  tech_deactivated: "text-[#FF3B30] bg-[#FF3B30]/10",
  template_deactivated: "text-[#FF3B30] bg-[#FF3B30]/10",
};

// Categorias para simplificar os botões de filtro
const FILTER_CATEGORIES = {
  all: [],
  motos: [
    "motorcycle_created",
    "motorcycle_completed",
    "client_linked",
    "observation_added",
    "checklist_updated",
  ],
  baterias: ["battery_linked", "battery_unlinked", "battery_replaced"],
  revisoes: [
    "revision_started",
    "revision_checklist_updated",
    "part_replaced",
    "revision_completed",
  ],
  cadastros: [
    "store_created",
    "store_updated",
    "store_deactivated",
    "technician_created",
    "tech_updated",
    "tech_deactivated",
    "checklist_template_created",
    "template_updated",
    "template_deactivated",
    "client_created",
    "client_updated",
  ],
};

export default function HistoryPage() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/history?limit=200`, {
        withCredentials: true,
      });
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredHistory =
    filter === "all"
      ? history
      : history.filter((h) =>
          FILTER_CATEGORIES[filter].includes(h.action_type),
        );

  const filterOptions = [
    { value: "all", label: "Todas as Ações" },
    { value: "motos", label: "Motos & Operação" },
    { value: "baterias", label: "Baterias" },
    { value: "revisoes", label: "Manutenção & Revisões" },
    { value: "cadastros", label: "Gestão de Cadastros" },
  ];

  const groupByDate = (items) => {
    const groups = {};
    items.forEach((item) => {
      const date = new Date(item.created_at).toLocaleDateString("pt-BR");
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(item);
    });
    return groups;
  };

  const groupedHistory = groupByDate(filteredHistory);

  if (loading) {
    return (
      <Layout title="Histórico">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Histórico">
      <div className="space-y-4 pb-4">
        {/* Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          <Funnel size={18} className="text-[#A1A1AA] flex-shrink-0" />
          {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              className={`px-4 py-2 rounded-full text-xs whitespace-nowrap transition-colors border ${
                filter === option.value
                  ? "bg-[#00E676]/10 text-[#00E676] border-[#00E676]/20 font-bold"
                  : "bg-[#1A1A1A] text-[#A1A1AA] border-white/10 hover:text-white hover:bg-white/5"
              }`}
              data-testid={`history-filter-${option.value}`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* History List */}
        {filteredHistory.length === 0 ? (
          <div className="card p-8 text-center mt-6">
            <ClockCounterClockwise
              size={48}
              className="mx-auto text-[#A1A1AA] mb-4 opacity-50"
            />
            <p className="text-[#A1A1AA]">
              Nenhum registro encontrado para este filtro.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedHistory).map(([date, items]) => (
              <div key={date}>
                <h3 className="text-sm font-bold text-[#A1A1AA] mb-3 sticky top-14 bg-[#0A0A0A] py-2 z-10">
                  {date}
                </h3>
                <div className="space-y-2">
                  {items.map((action, idx) => {
                    const IconComponent =
                      ACTION_ICONS[action.action_type] || ClockCounterClockwise;
                    const colorClass =
                      ACTION_COLORS[action.action_type] ||
                      "text-[#A1A1AA] bg-[#1A1A1A] border border-white/10";

                    return (
                      <div
                        key={idx}
                        className="card p-3 sm:p-4 flex items-start gap-3 sm:gap-4 transition-colors hover:bg-[#1A1A1A]"
                        data-testid={`history-item-${idx}`}
                      >
                        <div
                          className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}
                        >
                          <IconComponent size={20} className="sm:w-6 sm:h-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm sm:text-base break-words text-white/90">
                            {action.description}
                          </p>
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-1.5">
                            <User
                              size={14}
                              className="text-[#A1A1AA] shrink-0"
                            />
                            <span className="text-xs font-medium text-[#A1A1AA] truncate">
                              {action.user_name}
                            </span>
                            <span className="text-xs text-[#A1A1AA] opacity-50">
                              •
                            </span>
                            <span className="text-xs text-[#A1A1AA] font-mono whitespace-nowrap">
                              {new Date(action.created_at).toLocaleTimeString(
                                "pt-BR",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
